import { ContentCopy } from '@mui/icons-material'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    SxProps,
    Theme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { DentalMarketingPlan } from '../../../services/models/inurance-plan'
import { DentalCoverage, Member } from '../../../services/models/member'
import { getDentalPlan } from '../../../services/productService'
import { DataPanel } from '../../DataPanel'
import OneStepForm, { DataEntry, FormResult, FormValueType } from '../../OneStepForm'
import { DentalProductDetailsDialog } from './DentalProductDetailsDialog'

interface ViewMemberDetailsDialogProps {
    data?: {
        member: Member
        coverage: DentalCoverage
    }
    open: boolean
    onClose: (memberId?: string) => void
}

const MemberDetailsDialog = (props: ViewMemberDetailsDialogProps) => {
    const { data, open, onClose } = props

    if (!data) {
        if (open) {
            onClose()
        }
        return <></>
    }

    const { member, coverage } = data

    const [formData, setFormData] = useState<{
        demographics: DataEntry[]
        address: DataEntry[]
        subscriber: DataEntry[]
        accumulator: DataEntry[]
        productBenefits: DataEntry[]
        product: DataEntry[]
        coverageStatus: DataEntry[]
    }>({
        demographics: [],
        address: [],
        subscriber: [],
        accumulator: [],
        productBenefits: [],
        product: [],
        coverageStatus: [],
    })
    const [formResults, setFormResults] = useState<{ [key: string]: FormResult }>({})
    const [submitDisabled, setSubmitDisabled] = useState(true)
    const [planDialogOpen, setPlanDialogOpen] = useState(false)
    const [planDialogData, setPlanDialogData] = useState<DentalMarketingPlan | null>(null)

    useEffect(() => {
        const fetchAndPopulate = async () => {
            const {
                data: { plan },
            } = await getDentalPlan(coverage.plan.id)
            setPlanDialogData(plan)
            const demographicsData: DataEntry[] = [
                {
                    key: 'name',
                    label: 'Name',
                    type: FormValueType.TEXT,
                    initialValue: `${member.lastName}, ${member.firstName}`,
                    readOnly: true,
                },
                {
                    key: 'dateOfBirth',
                    label: 'Date Of Birth',
                    type: FormValueType.DATE,
                    initialValue: member.dateOfBirth,
                    readOnly: true,
                },
                {
                    key: 'gender',
                    label: 'Gender',
                    type: FormValueType.TEXT,
                    initialValue: member.gender,
                    readOnly: true,
                },
                {
                    key: 'ssn',
                    label: 'SSN',
                    type: FormValueType.TEXT,
                    initialValue: member.ssn,
                    readOnly: true,
                },
            ]
            const addressData: DataEntry[] = [
                {
                    key: 'addressLine1',
                    label: 'Line 1',
                    type: FormValueType.TEXT,
                    initialValue: member.address.addressLine1,
                    readOnly: true,
                },
                {
                    key: 'addressLine2',
                    label: 'Line 2',
                    type: FormValueType.TEXT,
                    initialValue: member.address.addressLine2,
                    readOnly: true,
                },
                {
                    key: 'city',
                    label: 'City',
                    type: FormValueType.TEXT,
                    initialValue: member.address.city,
                    readOnly: true,
                },
                {
                    key: 'state',
                    label: 'State',
                    type: FormValueType.TEXT,
                    initialValue: member.address.state,
                    readOnly: true,
                },
                {
                    key: 'zip',
                    label: 'Zip',
                    type: FormValueType.TEXT,
                    initialValue: member.address.zipCode,
                    readOnly: true,
                },
            ]
            const subscriberData: DataEntry[] = [
                {
                    key: 'relationShip',
                    label: 'RelationShip',
                    type: FormValueType.TEXT,
                    initialValue: coverage.relationshipToSubscriber?.toUpperCase(),
                    readOnly: true,
                },
                {
                    key: 'subscriberMemberId',
                    label: 'Subscriber Member Id',
                    type: FormValueType.TEXT,
                    initialValue: coverage.subscriberId,
                    readOnly: true,
                },
                {
                    key: 'memberId',
                    label: 'MemberId',
                    type: FormValueType.TEXT,
                    initialValue: coverage.subscriberId,
                    readOnly: true,
                },
                {
                    key: 'groupNumber',
                    label: 'Plan / Group Number',
                    type: FormValueType.TEXT,
                    initialValue: coverage?.plan.groupNumber,
                    readOnly: true,
                },
                {
                    key: 'employerGroupName',
                    label: 'Employer Name',
                    type: FormValueType.TEXT,
                    initialValue: member.employerGroupName,
                    readOnly: true,
                },
            ]

            const accumulatorData: DataEntry[] = [
                {
                    key: 'outOfPocketIndividual',
                    label: 'Out of Pocket - Individual',
                    type: FormValueType.CURRENCY,
                    initialValue: coverage.plan.maxOutOfPocket.individual,
                    readOnly: true,
                },
                {
                    key: 'outOfPocketFamily',
                    label: 'Out of Pocket - Family',
                    type: FormValueType.CURRENCY,
                    initialValue: coverage.plan.maxOutOfPocket.family,
                    readOnly: true,
                },
                {
                    key: 'deductibleSpendIndividual',
                    label: 'Deductible Spend Individual',
                    type: FormValueType.CURRENCY,
                    initialValue: coverage.plan.deductible.individual,
                    readOnly: true,
                },
                {
                    key: 'deductibleSpendFamily',
                    type: FormValueType.CURRENCY,
                    label: 'Deductible Spend - Family',
                    initialValue: coverage.plan.deductible.family,
                    readOnly: true,
                },
                {
                    key: 'totalPlanSpend',
                    label: 'Total Plan Spend',
                    type: FormValueType.CURRENCY,
                    initialValue: coverage.plan.totalMaximum,
                    readOnly: true,
                },
                {
                    key: 'coveredPreventiveVisitCountRemaining',
                    type: FormValueType.NUMBER,
                    label: 'Covered Preventive Visit Remaining',
                    initialValue: (coverage.plan.benefits ?? [])
                        .filter((b) => b.category === 'preventive')
                        ?.reduce((n, { totalServices }) => n + totalServices, 0),
                    readOnly: true,
                },
            ]

            const productYear = new Date().getFullYear()
            const productBenefitData: DataEntry[] = [
                {
                    key: 'maxOutOfPocketIndividual',
                    label: 'Max OOP Individual',
                    type: FormValueType.CURRENCY,
                    initialValue: 0,
                    readOnly: true,
                },
                {
                    key: 'maxOutOfPocketFamily',
                    label: 'Max OOP Family',
                    type: FormValueType.CURRENCY,
                    initialValue: 0,
                    readOnly: true,
                },
                {
                    key: 'deductibleIndividual',
                    label: 'Deductible - Individual',
                    type: FormValueType.CURRENCY,
                    initialValue: coverage.plan.deductible.individual,
                    readOnly: true,
                },
                {
                    key: 'deductibleFamily',
                    type: FormValueType.CURRENCY,
                    label: 'Deductible - Family',
                    initialValue: coverage.plan.deductible.family,
                    readOnly: true,
                },
                {
                    key: 'totalPlanSpendRemaining',
                    label: 'Total Plan Spend Remaining',
                    type: FormValueType.CURRENCY,
                    initialValue: coverage.plan.totalMaximum,
                    readOnly: true,
                },
                {
                    key: 'coveredPreventiveVisitCount',
                    type: FormValueType.NUMBER,
                    label: 'Covered Preventive Visit Count',
                    initialValue: (coverage.accumulators ?? [])
                        .filter((b) => b.category === 'preventive')
                        ?.reduce((n, { totalRemaining }) => n + totalRemaining, 0),
                    readOnly: true,
                },
                {
                    key: 'currentPlanYear',
                    type: FormValueType.NUMBER,
                    label: 'Current Plan Year',
                    initialValue: productYear,
                    readOnly: true,
                },
            ]

            const productData: DataEntry[] = [
                {
                    key: 'planYear',
                    label: 'Plan Year',
                    type: FormValueType.NUMBER,
                    initialValue: productYear,
                    readOnly: true,
                },
                {
                    key: 'name',
                    label: 'Name',
                    type: FormValueType.TEXT,
                    initialValue: coverage.plan?.planName,
                    readOnly: true,
                },
                {
                    key: 'networkName',
                    label: 'Network Name',
                    type: FormValueType.TEXT,
                    initialValue: coverage.plan?.networkName,
                    readOnly: true,
                },
                {
                    key: 'payerName',
                    type: FormValueType.TEXT,
                    label: 'Payer',
                    initialValue: member.payerName,
                    readOnly: true,
                },
            ]

            const coverageStatusData: DataEntry[] = [
                {
                    key: 'coverageEffectiveStartDate',
                    label: 'Start Date',
                    type: FormValueType.DATE,
                    initialValue: coverage.effectiveStartDate,
                    readOnly: true,
                },
                {
                    key: 'coverageEffectiveEndDate',
                    label: 'End Date',
                    type: FormValueType.DATE,
                    initialValue: coverage.effectiveEndDate,
                    readOnly: true,
                },
                {
                    key: 'active',
                    label: 'Active Status',
                    type: FormValueType.SWITCH,
                    initialValue: coverage.isActive,
                    readOnly: true,
                },
            ]
            setFormData({
                demographics: demographicsData,
                address: addressData,
                subscriber: subscriberData,
                accumulator: accumulatorData,
                productBenefits: productBenefitData,
                product: productData,
                coverageStatus: coverageStatusData,
            })
        }
        fetchAndPopulate()
    }, [props])

    const handleFormChange = (formName: string) => (update: FormResult) => {
        const newState = { ...formResults, [formName]: update }
        const stateIsPopulated = Object.keys(newState).reduce((acc, key) => {
            if (acc) return true
            return Object.keys(newState[key]).length > 0
        }, false)
        setSubmitDisabled(!stateIsPopulated)
        setFormResults(newState)
    }

    const renderFormSection = (
        name:
            | 'demographics'
            | 'address'
            | 'subscriber'
            | 'accumulator'
            | 'productBenefits'
            | 'product'
            | 'coverageStatus',
        title?: string,
        sx?: SxProps<Theme>,
    ) => (
        <Box sx={sx ? sx : { margin: 2 }}>
            {title && (
                <DataPanel
                    title={title}
                    content={
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <OneStepForm
                                    formData={formData[name]}
                                    controlled
                                    onChange={handleFormChange(name)}
                                />
                            </Grid>
                        </Grid>
                    }
                ></DataPanel>
            )}
        </Box>
    )

    return (
        <>
            <DentalProductDetailsDialog
                open={planDialogOpen}
                onClose={() => setPlanDialogOpen(false)}
                dentalProduct={planDialogData as DentalMarketingPlan}
            />
            <Dialog open={open} maxWidth="xl">
                <DialogTitle>{`Member ${member.firstName} ${member.lastName}`}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                {renderFormSection('demographics', 'Demographics')}
                                {renderFormSection('address', 'Address')}
                                <Box sx={{ margin: 2 }}>
                                    <DataPanel
                                        title={'Primary Subscriber'}
                                        content={
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <OneStepForm
                                                        formData={formData['subscriber']}
                                                        controlled
                                                        onChange={handleFormChange('subscriber')}
                                                    />
                                                </Grid>
                                                {coverage?.relationshipToSubscriber?.toUpperCase() !==
                                                    'SELF' && (
                                                    <Grid container justifyContent={'flex-end'}>
                                                        <Button
                                                            variant="text"
                                                            sx={{ backgroundColor: 'transparent' }}
                                                            disableElevation={true}
                                                            endIcon={<ContentCopy />}
                                                            onClick={() => {
                                                                onClose(coverage.subscriberId)
                                                            }}
                                                        >
                                                            View Record
                                                        </Button>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        }
                                    ></DataPanel>
                                </Box>
                                {renderFormSection('accumulator', 'Accumulators')}
                                {renderFormSection('productBenefits', 'Product Benefit Reference')}
                                <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                                    <Box sx={{ margin: 2 }}>
                                        <DataPanel
                                            title={'Product Reference'}
                                            content={
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <OneStepForm
                                                            formData={formData['product']}
                                                            controlled
                                                            onChange={handleFormChange('product')}
                                                        />
                                                    </Grid>
                                                    <Grid container justifyContent={'flex-end'}>
                                                        <Button
                                                            variant="text"
                                                            sx={{ backgroundColor: 'transparent' }}
                                                            disableElevation={true}
                                                            onClick={() => {
                                                                setPlanDialogData(planDialogData)
                                                                setPlanDialogOpen(true)
                                                            }}
                                                        >
                                                            More Details
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            }
                                        ></DataPanel>
                                    </Box>
                                    {renderFormSection('coverageStatus', 'Coverage Status')}
                                </Box>
                            </Grid>
                        </Grid>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button disabled={submitDisabled} onClick={() => {}}>
                        Validate
                    </Button>
                    <Button disabled={submitDisabled} onClick={() => {}}>
                        Update
                    </Button>
                    <Button onClick={() => onClose()}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default MemberDetailsDialog
